import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { BackLinkComponent } from '../shared/components/back-link/back-link.component';
import { LoadingSpinnerComponent } from '../shared/components/loading-spinner/loading-spinner.component';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Subscription } from 'rxjs';
import { JobService } from '../services/job.service';
import { ResizableTextareaComponent } from '../shared/components/resizable-textarea/resizable-textarea.component';
import * as mapboxgl from 'mapbox-gl';
import { FeatureCollection } from 'geojson';
import { MarkdownPipe } from '../markdown.pipe';
import { CatchTableComponent } from '../shared/components/catch-table/catch-table.component';

interface TrackResponse {
  coordinates: [number, number][];
}
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { environment } from '../../environments/environment';

interface CatchEntry {
  count: number;
  display_name: string;
  color: string;
}

interface ConditionsSummary {
  temperature: { min: number | null; max: number | null; avg: number | null };
  water_temperature: { min: number | null; max: number | null };
  wind_speed_most_common: string | null;
  wind_direction_most_common: string | null;
  tide_cycles: string[];
  short_forecasts: string[];
}

interface TripReport {
  trip_id: number;
  name: string | null;
  status: string;
  start_time: string | null;
  end_time: string | null;
  generated_at: string | null;
  duration_minutes: number | null;
  distance_miles: number | null;
  total_catches: number | null;
  catch_summary: Record<string, CatchEntry> | null;
  conditions_summary: ConditionsSummary | null;
  report_text: string | null;
  system_prompt: string | null;
  user_prompt: string | null;
  user_notes?: string | null;
}

@Component({
  selector: 'app-trip-detail',
  templateUrl: './trip-detail.component.html',
  styleUrls: ['./trip-detail.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, DatePipe, DecimalPipe, MatProgressSpinnerModule, MatButtonModule, MarkdownPipe, BackLinkComponent, LoadingSpinnerComponent, CatchTableComponent, ResizableTextareaComponent],
})
export class TripDetailComponent implements OnInit, OnDestroy {
  report: TripReport | null = null;
  isLoading = true;
  errorMessage: string | null = null;
  reportNotFound = false;
  tripId!: number;
  isRegenerating = false;
  tripName: string | null = null;
  editingName = false;
  nameValue = '';
  isSavingName = false;

  userNotes = '';
  isSavingNotes = false;
  dwellEvents: any[] = [];
  isDetectingSpots = false;
  detectSpotsStatus: string | null = null;

  catchFeatures: GeoJSON.Feature[] = [];
  currentCatchIndex = 0;
  isEditMode = false;
  isSavingLocation = false;

  private map!: mapboxgl.Map;
  private popup!: mapboxgl.Popup;
  private draggableMarker: mapboxgl.Marker | null = null;
  private allFeatures: GeoJSON.Feature[] = [];
  private jobSub: Subscription | null = null;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private jobService: JobService,
  ) {}

  ngOnInit(): void {
    this.tripId = Number(this.route.snapshot.paramMap.get('id'));
    this.jobSub = this.jobService.getCompletions$('trip', this.tripId).subscribe(() => {
      this.loadReport();
    });
    this.loadReport(true);
  }

  loadReport(withMap = false): void {
    this.http.get<TripReport>(`${environment.apiUrl}/trip_report/${this.tripId}`).subscribe({
      next: (data) => {
        this.tripName = (data as any).name ?? null;
        this.userNotes = (data as any).user_notes ?? '';
        if ((data as any).status === 'not_found') {
          this.reportNotFound = true;
        } else {
          this.report = data;
          this.reportNotFound = false;
        }
        this.isLoading = false;
        this.cdr.detectChanges();
        if (withMap) {
          this.initMap();
          this.loadDwellEvents();
        } else {
          this.loadDwellEvents();
        }
      },
      error: () => {
        this.errorMessage = 'Failed to load trip report.';
        this.isLoading = false;
      }
    });
  }

  private initMap(): void {
    const query = { query: [{ trip_id: this.tripId }] };

    forkJoin({
      tracks: this.http.post<TrackResponse>(`${environment.apiUrl}/tracks`, query),
      catches: this.http.post<FeatureCollection>(`${environment.apiUrl}/catches`, query),
    }).subscribe(({ tracks, catches }) => {
      const coords = tracks.coordinates;

      // Compute bounds from all track coordinates
      let bounds: mapboxgl.LngLatBounds | null = null;
      if (coords.length > 0) {
        bounds = new mapboxgl.LngLatBounds(coords[0], coords[0]);
        for (const c of coords) bounds.extend(c);
      }
      const center = bounds ? bounds.getCenter() : { lng: -76.430749, lat: 39.001904 };

      this.map = new mapboxgl.Map({
        accessToken: environment.mapbox.accessToken,
        container: 'trip-map',
        style: 'mapbox://styles/mapbox/standard-satellite',
        center: [center.lng, center.lat],
        zoom: 13,
        dragRotate: false,
        touchPitch: false
      });

      this.popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false });

      this.map.on('load', () => {
        // Route line — backend returns {coordinates:[...]}, must wrap as Feature+LineString
        this.map.addSource('route', {
          type: 'geojson',
          lineMetrics: true,
          data: {
            type: 'Feature',
            properties: {},
            geometry: { type: 'LineString', coordinates: coords },
          },
        });
        this.map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-width': 2,
            'line-opacity': 0.8,
            'line-gradient': [
              'interpolate', ['linear'], ['line-progress'],
              0, 'blue', 0.1, 'royalblue', 0.3, 'cyan',
              0.5, 'lime', 0.7, 'yellow', 1, 'red',
            ],
          },
        });

        const catchFeatures: FeatureCollection = catches;
        this.allFeatures = catchFeatures.features ?? [];
        this.map.addSource('fish-data', { type: 'geojson', data: catchFeatures });
        this.map.addLayer({
          id: 'fishes',
          type: 'circle',
          source: 'fish-data',
          paint: {
            'circle-radius': 6,
            'circle-color': ['get', 'fish_type_display_color'],
            'circle-stroke-width': 1,
            'circle-stroke-color': '#ffffff',
          },
        });
        this.map.addLayer({
          id: 'selected-catch',
          type: 'circle',
          source: 'fish-data',
          paint: {
            'circle-radius': 11,
            'circle-color': 'transparent',
            'circle-stroke-width': 3,
            'circle-stroke-color': '#ffffff',
          },
          filter: ['==', ['id'], 0],
        });

        this.catchFeatures = [...this.allFeatures]
          .sort((a, b) => new Date(a.properties?.['time']).getTime() - new Date(b.properties?.['time']).getTime());
        if (this.catchFeatures.length > 0) {
          this.selectCatch(0);
        }
        this.cdr.detectChanges();

        // Popup on hover
        this.map.on('mousemove', 'fishes', (e) => {
          this.map.getCanvas().style.cursor = 'pointer';
          if (!e.features?.length) return;
          const feature = e.features[0];
          if (feature.geometry.type !== 'Point') return;
          const coords2 = (feature.geometry as GeoJSON.Point).coordinates as [number, number];
          const p = feature.properties ?? {};
          this.popup
            .setLngLat(coords2)
            .setHTML(`
              <div>
                <strong>Fish Type:</strong> ${p['fish_type_full_name'] ?? 'Unknown'}<br>
                <strong>Temperature:</strong> ${p['temperature'] ?? 'N/A'}°F<br>
                <strong>Water Temp:</strong> ${p['water_temperature'] ?? 'N/A'}°F ${p['water_temperature_trend'] ?? ''}<br>
                <strong>Tide:</strong> ${p['tide_cycle'] ?? 'Unknown'}<br>
                <strong>Wind:</strong> ${p['wind_speed'] ?? 'N/A'} ${p['wind_direction'] ?? ''}<br>
                <strong>Forecast:</strong> ${p['short_forecast'] ?? 'No forecast'}<br>
                <strong>Time:</strong> ${p['time'] ? new Date(p['time']).toLocaleString() : 'Unknown'}
              </div>`)
            .addTo(this.map);
        });

        this.map.on('mouseleave', 'fishes', () => {
          this.map.getCanvas().style.cursor = '';
          this.popup.remove();
        });

        if (bounds) {
          this.map.fitBounds(bounds, { padding: 50, maxZoom: 16 });
        }
      });
    });
  }

  selectCatch(index: number): void {
    this.cancelEdit();
    this.currentCatchIndex = index;
    const feature = this.catchFeatures[index];
    if (!feature || feature.geometry.type !== 'Point') return;
    const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;
    this.map.flyTo({ center: [lng, lat], zoom: Math.max(this.map.getZoom(), 15) });
    this.map.setFilter('selected-catch', ['==', ['id'], feature.id as number]);
    this.cdr.detectChanges();
  }

  startEdit(): void {
    const feature = this.catchFeatures[this.currentCatchIndex];
    if (!feature || feature.geometry.type !== 'Point') return;
    const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;
    this.draggableMarker = new mapboxgl.Marker({ draggable: true, color: '#ff6600' })
      .setLngLat([lng, lat])
      .addTo(this.map);
    this.isEditMode = true;
    this.cdr.detectChanges();
  }

  cancelEdit(): void {
    this.draggableMarker?.remove();
    this.draggableMarker = null;
    this.isEditMode = false;
    this.cdr.detectChanges();
  }

  saveLocation(): void {
    if (!this.draggableMarker) return;
    const lngLat = this.draggableMarker.getLngLat();
    const feature = this.catchFeatures[this.currentCatchIndex];
    const catchId = feature.id as number;
    this.isSavingLocation = true;
    this.http.patch(`${environment.apiUrl}/catches/${catchId}/location`, { lat: lngLat.lat, long: lngLat.lng })
      .subscribe({
        next: () => {
          (feature.geometry as GeoJSON.Point).coordinates = [lngLat.lng, lngLat.lat];
          (this.map.getSource('fish-data') as mapboxgl.GeoJSONSource).setData({
            type: 'FeatureCollection',
            features: this.allFeatures,
          });
          this.isSavingLocation = false;
          this.cancelEdit();
        },
        error: () => {
          this.isSavingLocation = false;
          this.cdr.detectChanges();
        },
      });
  }

  startEditName(): void {
    this.nameValue = this.tripName ?? '';
    this.editingName = true;
  }

  cancelEditName(): void {
    this.editingName = false;
  }

  saveName(): void {
    this.isSavingName = true;
    this.http.patch<{ name: string | null }>(`${environment.apiUrl}/trips/${this.tripId}/name`, { name: this.nameValue || null })
      .subscribe({
        next: (res) => {
          this.tripName = res.name;
          this.editingName = false;
          this.isSavingName = false;
          this.cdr.detectChanges();
        },
        error: () => { this.isSavingName = false; },
      });
  }

  saveNotes(): void {
    this.isSavingNotes = true;
    this.cdr.detectChanges();
    this.http.patch(`${environment.apiUrl}/trips/${this.tripId}/notes`, { user_notes: this.userNotes })
      .subscribe({
        next: () => { this.isSavingNotes = false; this.cdr.detectChanges(); },
        error: () => { this.isSavingNotes = false; this.cdr.detectChanges(); },
      });
  }

  private loadDwellEvents(): void {
    this.http.get<any[]>(`${environment.apiUrl}/trips/${this.tripId}/dwell`).subscribe({
      next: (events) => { this.dwellEvents = events; this.cdr.detectChanges(); },
      error: () => {}
    });
  }

  detectSpots(): void {
    this.isDetectingSpots = true;
    this.detectSpotsStatus = null;
    this.cdr.detectChanges();
    this.http.post<any>(`${environment.apiUrl}/trips/${this.tripId}/dwell/compute`, {}).subscribe({
      next: (res) => {
        if (res.job_id) this.jobService.register({ id: res.job_id, job_type: 'trip_dwell', entity_type: 'trip', entity_id: this.tripId, status: 'queued', message: 'Detecting fishing spots...', created_at: new Date().toISOString(), completed_at: null });
        this.detectSpotsStatus = null;
        this.isDetectingSpots = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.detectSpotsStatus = 'Failed to queue dwell detection.';
        this.isDetectingSpots = false;
        this.cdr.detectChanges();
      }
    });
  }

  regenerate(): void {
    this.isRegenerating = true;
    this.http.post<any>(`${environment.apiUrl}/trip_report/${this.tripId}/generate`, {})
      .subscribe({
        next: (res) => {
          if (res.job_id) this.jobService.register({ id: res.job_id, job_type: 'trip_report', entity_type: 'trip', entity_id: this.tripId, status: 'queued', message: 'Generating trip report...', created_at: new Date().toISOString(), completed_at: null });
          this.isRegenerating = false;
        },
        error: () => { this.isRegenerating = false; },
      });
  }

  get catchEntries(): Array<{ key: string } & CatchEntry> {
    if (!this.report?.catch_summary) return [];
    return Object.entries(this.report.catch_summary).map(([key, val]) => ({ key, ...val }))
      .sort((a, b) => b.count - a.count);
  }

  get totalCatches(): number {
    return this.catchEntries.reduce((sum, e) => sum + e.count, 0);
  }

  formatDuration(minutes: number | null): string {
    if (minutes === null) return '—';
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }

  ngOnDestroy(): void {
    this.jobSub?.unsubscribe();
    this.map?.remove();
  }
}
